import React, {useCallback, useEffect, useMemo} from 'react';
import {
  Layout,
  ResponsiveLayout,
  ResponsiveLayoutStyles,
} from '@di-nxt/components';
import {TeamsSelector} from '../teams';
import {Authenticated} from '../authenticated';
import {SideBarButton} from './SideBarButton';
import {ProfileAvatar} from '../profileavatar';
import {useAppNavigationContext} from '../../navigation/hooks';
import {Team} from '../../services';
import {useGotoMembers, useGotoSchedule} from '../../scenes';
import {t} from '../../localization';
import {useLocation} from 'react-router-dom';
import {SidebarMenuItems} from './types';

const SideBarWidth = 200;

export const SideBar: React.FC = () => {
  const {
    selectedTeam,
    setSelectedTeam,
    sidebarVisible,
    setSidebarVisible,
  } = useAppNavigationContext();

  const location = useLocation();
  let activeMenuItem: SidebarMenuItems = 'Courier';
  if (location.pathname.startsWith('/members')) {
    activeMenuItem = 'Courier';
  } else if (location.pathname.startsWith('/schedule')) {
    activeMenuItem = 'Schedule';
  } else if (location.pathname.startsWith('/teampreferences')) {
    activeMenuItem = 'TeamPreferences';
  }

  // Callbacks and event handlers
  const closeSidebar = useCallback(() => setSidebarVisible(false), [
    setSidebarVisible,
  ]);

  // Navigate to members
  const gotoMembers = useGotoMembers();
  const handleGotoMembers = useCallback(() => {
    if (selectedTeam) {
      gotoMembers(selectedTeam.id);

      closeSidebar();
    }
  }, [closeSidebar, gotoMembers, selectedTeam]);

  // Navigate to schedule
  const gotoSchedule = useGotoSchedule();
  const handleGotoSchedule = useCallback(() => {
    if (selectedTeam) {
      gotoSchedule(selectedTeam.id);

      closeSidebar();
    }
  }, [closeSidebar, gotoSchedule, selectedTeam]);

  // Select Team
  const handleSelectTeam = useCallback(
    (team: Team) => {
      setSelectedTeam(team);
      switch (activeMenuItem) {
        case 'Schedule': {
          gotoSchedule(team.id);
          break;
        }
        case 'Courier': {
          gotoMembers(team.id);
          break;
        }
      }
      closeSidebar();
    },
    [activeMenuItem, closeSidebar, gotoMembers, gotoSchedule, setSelectedTeam],
  );

  const responsiveStyle = useMemo((): ResponsiveLayoutStyles => {
    return {
      ...responsiveStyles,
      mobile: {
        ...(sidebarVisible
          ? {
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              width: '70%',
              zIndex: 1000,
            }
          : {
              position: 'fixed',
              left: -SideBarWidth,
              top: 0,
              bottom: 0,
              width: SideBarWidth,
            }),
      },
    };
  }, [sidebarVisible]);

  // Listen to click events to hide sidebar
  useEffect(() => {
    if (sidebarVisible) {
      document.addEventListener('click', closeSidebar);
    } else {
      document.removeEventListener('click', closeSidebar);
    }
    return () => document.removeEventListener('click', closeSidebar);
  }, [sidebarVisible, closeSidebar]);

  return (
    <Authenticated>
      <ResponsiveLayout styles={responsiveStyle}>
        <Layout
          flex
          backgroundColor="background_mono_primary"
          borderRightColor={'border_mono_15'}
          borderRightWidth={'1'}
          borderRightStyle="solid">
          <Layout flex vertical>
            {/** Team selector */}
            <Layout
              horizontal
              height="56"
              paddingHorizontal={'8'}
              paddingVertical="16">
              <TeamsSelector
                selectedTeamId={selectedTeam?.id}
                onChangeTeam={handleSelectTeam}
              />
            </Layout>
            {/** Menu */}
            <Layout vertical flex>
              {selectedTeam && (
                <>
                  <SideBarButton
                    title={t('components.sidebar.titles.schedule')}
                    selected={activeMenuItem === 'Schedule'}
                    onSelected={handleGotoSchedule}
                  />
                  <SideBarButton
                    title={t('components.sidebar.titles.couriers')}
                    selected={activeMenuItem === 'Courier'}
                    onSelected={handleGotoMembers}
                  />
                  <SideBarButton
                    title={t('components.sidebar.titles.teampreferences')}
                    selected={activeMenuItem === 'TeamPreferences'}
                  />
                </>
              )}
            </Layout>
            {/** Profile */}
            <Layout alignSelf="flex-start" width="100%">
              <ProfileAvatar />
            </Layout>
          </Layout>
        </Layout>
      </ResponsiveLayout>
    </Authenticated>
  );
};

const responsiveStyles: ResponsiveLayoutStyles = {
  desktop: {
    width: SideBarWidth,
  },
  tablet: {
    width: SideBarWidth,
  },
  mobile: {
    width: '70%',
  },
};
